import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import React, { useEffect, useState } from "react";
import { Option } from "@/types/Shared/select";
import { useReadProjectGroups } from "@/libs/react-query/hooks/useProjectQuery";
import { useCreateProjectGroupsTasks } from "@/libs/react-query/hooks/useTaskQuery";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { CreateProjectGroupsTasksRequest } from "@/types/task";
import { Form } from "@/components/ui/form";

import ControlledInput from "@/components/Common/form/ControlledInput";
import ControlledAutoComplete from "./ControlledAutoComplete";
import ControlledDatePicker from "@/components/Common/form/ControlledDatePicker";
import ControlledTextarea from "@/components/Common/form/ControlledTextarea";
import { showToast } from "@/utils/toastUtils";
import { CurrentUserContent } from "@/types/setting";
import useUserStore from "@/stores/User";

const formSchema = z.object({
  title: z.string().nonempty("작업 이름을 설정하세요").default(""),
  due_date: z.date({ required_error: "마감일을 선택하세요" }),
  memo: z.string().default(""),
  assignees: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
      })
    )
    .min(1, "작업자를 추가하세요")
    .default([]),
});

type FormSchema = z.infer<typeof formSchema>;

export default function CreateTaskDialog({
  open,
  setOpen,
  groupId,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  groupId: string;
}) {
  const [commands, setCommands] = useState<Option[]>([]);
  const [searchName, setSearchName] = useState<string>("");

  const storedUser: CurrentUserContent = useUserStore((state) => state.user);

  const { data: { users = [] } = {} } = useReadProjectGroups(groupId, true);
  const { mutate } = useCreateProjectGroupsTasks(groupId);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      memo: "",
      assignees: [],
    },
  });
  const {
    handleSubmit,
    formState: { isValid, isDirty },
  } = form;

  useEffect(() => {
    if (users.length === 0) return;

    try {
      const parseCommands = users.map((item) => ({
        value: item.user_id,
        label: item.user_name,
        ...item,
      }));

      setCommands(parseCommands);
    } catch (error) {
      console.error("error > ", error);
    }
  }, [users]);

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open]);

  function onSubmit(values: FormSchema) {
    const data: CreateProjectGroupsTasksRequest = {
      ...values,
      due_date: Math.floor(values.due_date.getTime() / 1000),
      assignees: values.assignees.map(({ value }) => ({
        user_id: value,
      })),
    };

    mutate(data, {
      onSuccess: () => {
        showToast({
          message: "작업 생성이 완료되었습니다",
        });
        setOpen(false);
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="[&>button]:hidden max-h-[780px] w-[640px] overflow-y-auto"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-center text-gray-A text-[24px] font-bold">
            작업 요청하기
          </DialogTitle>
          <DialogDescription />
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="grid w-full gap-4">
            <ControlledInput
              name="title"
              label="작업 이름 설정하기"
              placeholder="작업 이름을 설정하세요"
              className="h-11"
            />
            <ControlledAutoComplete
              name="assignees"
              label="작업자 추가하기"
              placeholder="이름으로 검색해보세요"
              commands={commands}
              inputValue={searchName}
              setInputValue={setSearchName}
            />
            <ControlledDatePicker
              name="due_date"
              label="마감일"
              placeholder="날짜를 선택하세요"
            />
            <ControlledTextarea
              label="작업 메모"
              name="memo"
              placeholder="작업에 대한 설명을 적어주세요"
              className="h-60 resize-none"
            />

            <DialogFooter className="flex sm:justify-center">
              <DialogClose asChild>
                <Button
                  type="button"
                  className="bg-gray-E hover:bg-gray-E/90 border border-gray-D text-black-100"
                >
                  취소하기
                </Button>
              </DialogClose>
              <Button
                type="submit"
                className="bg-dark-red hover:bg-dark-red/90 border border-dark-red disabled:bg-gray-C disabled:border-gray-C"
                disabled={!isValid || !isDirty}
              >
                요청하기
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
